import {
  type ProviderLinkedData,
  type ProviderLinkedDataFailureReason,
  type ProviderLinkedDataPageRequest,
} from '@/api/core/provider-linked-data';
import { isApiError } from '@/api/http/errors';
import { InlineLoading } from '@/components/inline-loading';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Button, DataTable, DetailPage, type DataTableColumn } from '@mgrvtechgroup/admin-ds';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

const ROOM_COLUMNS: DataTableColumn<ProviderLinkedData['rooms'][number]>[] = [
  {
    key: 'provider_room_id',
    header: '이지렘스 객실 ID',
    sortable: false,
    cell: (room) => room.provider_room_id,
  },
  { key: 'room_number', header: '호수', sortable: false, cell: (room) => room.room_number },
  { key: 'sales_kind', header: '판매 종류', sortable: false, cell: (room) => room.sales_kind },
];

type ReadState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'result'; data: ProviderLinkedData }
  | { status: 'error'; message: string };

const failureLabels: Record<ProviderLinkedDataFailureReason, string> = {
  AUTHENTICATION_UNAVAILABLE: '외부 서비스 접속에 필요한 인증 정보를 확인할 수 없습니다.',
  AUTHENTICATION_DENIED: '외부 서비스에서 인증을 거부했습니다.',
  TIMEOUT: '외부 서비스 응답 시간이 초과되었습니다.',
  CONNECTION_FAILED: '외부 서비스에 연결하지 못했습니다.',
  INVALID_RESPONSE: '외부 서비스에서 받은 내용을 확인할 수 없습니다.',
  PROJECT_SCOPE_MISMATCH: '요청한 프로젝트와 받은 내용의 프로젝트가 일치하지 않습니다.',
  REDIRECT_REJECTED: '다른 주소로 이동하려는 시도가 있어 안전을 위해 조회를 중단했습니다.',
  ENVIRONMENT_UNSUPPORTED: '현재 환경의 객실 조회를 지원하지 않습니다.',
  READ_CAPABILITY_UNAVAILABLE: '현재 객실 조회 기능을 사용할 수 없습니다.',
};

function requestError(error: unknown) {
  if (isApiError(error)) {
    if (error.status === 401) return '세션이 만료되었습니다. 다시 로그인해 주세요.';
    if (error.status === 403) return '현재 지점의 조회 권한이 없습니다.';
    if (error.status === 404) return '지점 정보를 찾을 수 없습니다.';
    if (error.status === 409) return '지점 정보가 변경되었습니다. 다시 확인해 주세요.';
    if (error.status === 422) return '지점 식별 정보를 확인할 수 없습니다.';
    if (error.status === 503) return '현재 조회를 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.';
    if (error.kind === 'invalid-response') return '현재 조회 결과를 확인할 수 없습니다.';
  }
  return '현재 조회를 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.';
}

function statusLabel(data: ProviderLinkedData) {
  switch (data.status) {
    case 'SUCCEEDED':
      return '이지렘스에 등록된 객실을 확인했습니다.';
    case 'FAILED':
      return '이지렘스 객실 조회에 실패했습니다.';
    case 'UNKNOWN':
      return '객실 조회 기능을 아직 확인할 수 없습니다.';
    case 'NOT_CONFIGURED':
      return '이지렘스 프로젝트 설정이 없습니다.';
  }
}

export interface ProviderLinkedDataSectionProps {
  /** Read the current Property/environment with the supplied signal and optional explicit page. */
  onRead: (
    signal: AbortSignal,
    page?: ProviderLinkedDataPageRequest,
  ) => Promise<ProviderLinkedData>;
  /** Changes whenever Property, environment, or binding generation changes. */
  scopeKey: string;
}

export function ProviderLinkedDataSection({ onRead, scopeKey }: ProviderLinkedDataSectionProps) {
  const [state, setState] = useState<ReadState>({ status: 'idle' });
  const [overflowOwners, setOverflowOwners] = useState({ outer: false, inner: false });
  const tableRegionRef = useRef<HTMLDivElement>(null);
  const tableContentRef = useRef<HTMLDivElement>(null);
  const scrollHintId = useId();
  const requestRef = useRef<AbortController | null>(null);
  const currentScopeRef = useRef(scopeKey);
  currentScopeRef.current = scopeKey;

  useEffect(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    setState({ status: 'idle' });
    return () => {
      requestRef.current?.abort();
      requestRef.current = null;
    };
  }, [scopeKey]);

  useLayoutEffect(() => {
    const region = tableRegionRef.current;
    const content = tableContentRef.current;
    if (
      state.status !== 'result' ||
      state.data.status !== 'SUCCEEDED' ||
      state.data.rooms.length === 0 ||
      !region ||
      !content
    ) {
      setOverflowOwners({ outer: false, inner: false });
      return;
    }

    const measureOverflow = () => {
      const regionOverflows = region.scrollWidth > region.clientWidth;
      const tableContainer = content.querySelector<HTMLElement>('[data-slot="table-container"]');
      const tableContainerOverflows =
        tableContainer !== null && tableContainer.scrollWidth > tableContainer.clientWidth;

      if (tableContainer) {
        if (tableContainerOverflows) {
          tableContainer.setAttribute('role', 'region');
          tableContainer.setAttribute('aria-label', '이지렘스 등록 객실 표 가로 스크롤 영역');
          tableContainer.setAttribute('tabindex', '0');
          tableContainer.setAttribute('aria-describedby', scrollHintId);
        } else {
          tableContainer.removeAttribute('role');
          tableContainer.removeAttribute('aria-label');
          tableContainer.removeAttribute('tabindex');
          tableContainer.removeAttribute('aria-describedby');
        }
      }

      setOverflowOwners((current) =>
        current.outer === regionOverflows && current.inner === tableContainerOverflows
          ? current
          : { outer: regionOverflows, inner: tableContainerOverflows },
      );
    };
    measureOverflow();
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(region);
    observer.observe(content);
    const tableContainer = content.querySelector<HTMLElement>('[data-slot="table-container"]');
    if (tableContainer) observer.observe(tableContainer);
    window.addEventListener('resize', measureOverflow);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureOverflow);
      tableContainer?.removeAttribute('role');
      tableContainer?.removeAttribute('aria-label');
      tableContainer?.removeAttribute('tabindex');
      tableContainer?.removeAttribute('aria-describedby');
    };
  }, [scrollHintId, state]);

  const read = async (page?: ProviderLinkedDataPageRequest) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    const requestScope = scopeKey;
    requestRef.current = controller;
    setState({ status: 'pending' });
    try {
      const data = await onRead(controller.signal, page);
      if (!controller.signal.aborted && currentScopeRef.current === requestScope)
        setState({ status: 'result', data });
    } catch (error) {
      if (!controller.signal.aborted && currentScopeRef.current === requestScope)
        setState({ status: 'error', message: requestError(error) });
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  };

  return (
    <DetailPage.Section
      aria-label="이지렘스 등록 객실"
      title="이지렘스에 등록된 객실 확인"
      description="이번 조회에서 확인한 실제 등록 객실만 표시합니다."
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => void read()}
          loading={state.status === 'pending'}
        >
          객실 확인
        </Button>
      }
    >
      {state.status === 'idle' ? (
        <p role="status" className="body text-muted-foreground">
          등록 객실을 아직 확인하지 않았습니다.
        </p>
      ) : state.status === 'pending' ? (
        <InlineLoading>등록된 객실을 확인하고 있습니다.</InlineLoading>
      ) : state.status === 'error' ? (
        <p role="alert" className="body text-warning-foreground">
          {state.message}
        </p>
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <p role={state.data.status === 'FAILED' ? 'alert' : 'status'} className="body">
            {statusLabel(state.data)}
          </p>
          {state.data.failure_reason && (
            <p className="body text-muted-foreground">{failureLabels[state.data.failure_reason]}</p>
          )}
          <dl className="grid min-w-0 gap-x-6 gap-y-2 sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="caption text-muted-foreground">선택 지점</dt>
              <dd className="body break-words">
                {[state.data.selected_property.label, state.data.selected_property.code]
                  .filter(Boolean)
                  .join(' · ') || '표시할 지점 정보가 없습니다'}
              </dd>
              <dd className="caption text-muted-foreground">
                환경 · {state.data.environment === 'dev' ? '개발' : '운영'}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="caption text-muted-foreground">프로젝트 코드</dt>
              <dd className="body break-words">
                설정된 코드 · {state.data.project.configured_code ?? '설정되지 않음'}
              </dd>
              <dd className="body break-words">
                확인한 코드 · {state.data.project.observed_code ?? '이번 조회에서 확인되지 않음'}
              </dd>
            </div>
            {state.data.observed_at && (
              <div className="min-w-0">
                <dt className="caption text-muted-foreground">이번 확인 시각</dt>
                <dd className="body break-words">
                  {formatDateTimeWithOffset(state.data.observed_at)}
                </dd>
              </div>
            )}
          </dl>
          <p className="caption text-muted-foreground">
            이지렘스 객실과 지점 객실의 연결 정보는 제공되지 않습니다.
          </p>
          {state.data.status === 'SUCCEEDED' ? (
            <>
              {state.data.rooms.length > 0 ? (
                <>
                  {(overflowOwners.outer || overflowOwners.inner) && (
                    <p id={scrollHintId} className="caption text-muted-foreground">
                      표를 좌우로 스크롤해 판매 종류를 확인할 수 있습니다.
                    </p>
                  )}
                  <div
                    ref={tableRegionRef}
                    role="region"
                    aria-label="이지렘스에 등록된 객실"
                    aria-describedby={overflowOwners.outer ? scrollHintId : undefined}
                    className="min-w-0 overflow-x-auto"
                    tabIndex={overflowOwners.outer ? 0 : undefined}
                  >
                    <div ref={tableContentRef} className="min-w-[32rem]">
                      <DataTable
                        columns={ROOM_COLUMNS}
                        data={state.data.rooms}
                        rowKey={(room) => room.provider_room_id}
                        empty={{
                          title: '이번 페이지에서 표시할 객실이 없습니다',
                          description: '다음 페이지에서 이어서 확인할 수 있습니다.',
                        }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <p role="status" className="body text-muted-foreground">
                  이번 페이지에서 표시할 객실이 없습니다.
                </p>
              )}
              <p className="caption text-muted-foreground">
                전체 확인 객실 {state.data.page.total_count ?? '확인되지 않음'}개
              </p>
              {state.data.page.next_cursor !== null && (
                <div>
                  <Button
                    variant="outline"
                    onClick={() =>
                      void read({
                        limit: state.data.page.limit,
                        cursor: state.data.page.next_cursor ?? undefined,
                      })
                    }
                  >
                    다음 객실
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </DetailPage.Section>
  );
}
